import type { MarketOutcome } from '@/stores/game'

export const SAME_COLOR_BALLS_MARKET_SLUGS = [
  'same-color-balls-incl-bonus',
  'same-color-balls-excl-bonus',
] as const

export type SameColorBallsMarketSlug = (typeof SAME_COLOR_BALLS_MARKET_SLUGS)[number]

export const BALL_COLOR_ORDER = [
  'blue',
  'brown',
  'green',
  'orange',
  'purple',
  'red',
  'yellow',
] as const

export type BallColorSlug = (typeof BALL_COLOR_ORDER)[number]

export function pickRandomBallColor(): BallColorSlug {
  const index = Math.floor(Math.random() * BALL_COLOR_ORDER.length)
  return BALL_COLOR_ORDER[index]
}

export interface BallColorStyle {
  label: string
  background: string
  border: string
  text: string
}

export const BALL_COLOR_STYLES: Record<BallColorSlug, BallColorStyle> = {
  blue: { label: 'BLUE', background: '#2563eb', border: '#1d4ed8', text: '#ffffff' },
  brown: { label: 'BROWN', background: '#92400e', border: '#78350f', text: '#ffffff' },
  green: { label: 'GREEN', background: '#16a34a', border: '#15803d', text: '#ffffff' },
  orange: { label: 'ORANGE', background: '#ea580c', border: '#c2410c', text: '#ffffff' },
  purple: { label: 'PURPLE', background: '#9333ea', border: '#7e22ce', text: '#ffffff' },
  red: { label: 'RED', background: '#dc2626', border: '#b91c1c', text: '#ffffff' },
  yellow: { label: 'YELLOW', background: '#eab308', border: '#ca8a04', text: '#1f2937' },
}

/** market_outcomes.slug values in DB (e.g. blue-balls, red-balls). */
export function getColorOutcomeSlug(color: BallColorSlug): string {
  return `${color}-balls`
}

export function parseColorFromOutcomeSlug(slug: string): BallColorSlug | null {
  const normalized = slug.toLowerCase().trim()

  if (normalized.endsWith('-balls')) {
    const color = normalized.replace(/-balls$/, '')
    if ((BALL_COLOR_ORDER as readonly string[]).includes(color)) {
      return color as BallColorSlug
    }
  }

  if ((BALL_COLOR_ORDER as readonly string[]).includes(normalized)) {
    return normalized as BallColorSlug
  }

  return null
}

export function normalizeSpecifier(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return ''
  }
  return String(value)
}

export function matchesSameColorOutcome(
  outcome: Pick<MarketOutcome, 'slug' | 'specifier'>,
  color: BallColorSlug,
  count: string
): boolean {
  return outcome.slug.toLowerCase() === getColorOutcomeSlug(color) &&
    normalizeSpecifier(outcome.specifier) === count
}

export function isSameColorBallsMarket(slug: string): boolean {
  return (SAME_COLOR_BALLS_MARKET_SLUGS as readonly string[]).includes(slug)
}

export function formatSameColorBetText(outcome: MarketOutcome): string {
  const color = parseColorFromOutcomeSlug(outcome.slug)
  const colorLabel = color ? BALL_COLOR_STYLES[color].label : outcome.text.toUpperCase()
  const count = normalizeSpecifier(outcome.specifier)
  return count ? `${count}x ${colorLabel}` : outcome.text
}

/**
 * Resolve the correct game_market row when multiple outcomes share the same market_outcomes.id.
 * Prefers game_market_id when provided; otherwise outcome_id + specifier.
 */
export function resolveMarketOutcome(
  outcomes: MarketOutcome[],
  outcomeId: string,
  specifier?: string | number | null,
  gameMarketId?: number | null
): MarketOutcome | undefined {
  if (gameMarketId) {
    const byGameMarket = outcomes.find((o) => o.game_market_id === gameMarketId)
    if (byGameMarket) {
      return byGameMarket
    }
  }

  const normalizedSpecifier = normalizeSpecifier(specifier)
  if (normalizedSpecifier) {
    return outcomes.find(
      (o) =>
        o.id.toString() === outcomeId &&
        normalizeSpecifier(o.specifier) === normalizedSpecifier
    )
  }

  return outcomes.find((o) => o.id.toString() === outcomeId)
}

export function buildSameColorMarketSelection(
  market: { outcomes: MarketOutcome[] },
  outcome: MarketOutcome
) {
  return {
    odds: Number(outcome.odds),
    outcome_id: outcome.id.toString(),
    specifier: normalizeSpecifier(outcome.specifier) || undefined,
    game_market_id: outcome.game_market_id ?? undefined,
  }
}
